/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type ReactNode, useRef } from 'react';

import { css as customCss } from '@compiled/react';

import { cssMap, cx, jsx } from '@atlaskit/css';
/* eslint-disable @repo/internal/react/require-jsdoc */
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { BrandHeading } from '../brand-heading';
import { HeroGridBackground } from '../hero/grid-background';
import { Image } from '../image';
import { PageContentContainer } from '../page-content-container';
import { RelativePosition } from '../relative-position';

const styles = cssMap({
	bannerContainer: {
		display: 'flex',
		width: '100%',
		justifyContent: 'center',
	},
	backgroundColor: {
		backgroundColor: token('elevation.surface.sunken'),
	},
	bannerWidth: {
		width: '100%',
		maxWidth: '980px',
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch',
		gap: token('space.200'),
		paddingBlock: token('space.500'),
		'@media (min-width: 30rem)': {
			flexDirection: 'row',
			alignItems: 'center',
			paddingBlock: token('space.500'),
		},
	},
	contentContainer: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		height: '100%',
		flex: 3,
		width: '100%',
		'@media (min-width: 30rem)': {
			minHeight: '220px',
		},
	},
	imageContainer: {
		flex: 2,
		width: '100%',
		maxWidth: '400px',
	},
	fullHeightBanner: {
		position: 'relative',
		overflow: 'hidden',
	},
	fullHeightLayout: {
		paddingBlock: token('space.0'),
		'@media (min-width: 30rem)': {
			minHeight: '354px',
			paddingBlock: token('space.0'),
		},
	},
	fullHeightContent: {
		paddingBlockStart: token('space.500'),
		'@media (min-width: 30rem)': {
			paddingBlockEnd: token('space.500'),
		},
	},
	fullHeightImageContainer: {
		position: 'relative',
		'@media (min-width: 30rem)': {
			position: 'static',
			flex: 3,
			maxWidth: '100%',
			alignSelf: 'stretch',
		},
	},
	overflowImageContainer: {
		height: '354px',
		flex: 'none',
		width: '100vw',
		maxWidth: '100vw',
		overflow: 'hidden',
		'@media (min-width: 30rem)': {
			height: 'auto',
			flex: 3,
			width: '100%',
			overflow: 'visible',
		},
	},
	fullHeightImage: {
		display: 'block',
		width: '100%',
		height: 'auto',
		'@media (min-width: 30rem)': {
			position: 'absolute',
			insetBlockStart: 0,
			insetInlineStart: '50%',
			height: '100%',
			width: '50%',
		},
	},
	edgeToEdgeWrapper: {
		position: 'absolute',
		zIndex: -1,
		insetBlockStart: 0,
		insetBlockEnd: 0,
		insetInlineStart: '50%',
		width: '100vw',
		pointerEvents: 'none',
	},
	eyebrow: {
		font: token('font.code'),
		color: token('color.text.subtle'),
	},
	titleRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.400'),
	},
	titleDecoration: {
		width: '120px',
		height: 'auto',
		minWidth: 0,
		flexShrink: 1,
	},
	decoratedTitle: {
		flexShrink: 0,
		maxWidth: '100%',
	},
});

const customEdgeToEdgeWrapperStyles = customCss({
	insetInlineEnd: 'auto',
	marginInlineStart: '-50vw',
});

/**
 * Both image variants are always in the markup and the `data-color-mode` the pre-paint script has
 * already set on `<html>` shows one, so the prerendered markup is identical for every reader.
 * Choosing the `src` in JS made the server emit the light image and a dark reader's first client
 * render the dark one, a hydration mismatch React 18 does not patch.
 *
 * The hidden variant is still fetched, one wasted request per page. That is deliberate: lazy-loading
 * would keep the hidden one from loading, but this is the hero image, and a lazy hero waits for
 * layout instead of being picked up by the browser's preload scanner.
 */
const lightImageStyles = customCss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- shows one variant by color mode without re-rendering
	'html[data-color-mode="dark"] &': {
		display: 'none',
	},
});

const darkImageStyles = customCss({
	display: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- shows one variant by color mode without re-rendering
	'html[data-color-mode="dark"] &': {
		display: 'block',
	},
});

const overflowArtworkStyles = customCss({
	position: 'absolute',
	insetBlockStart: '50%',
	width: 'var(--banner-artwork-width)',
	height: 'auto',
	maxWidth: 'none',
	transform: 'translateY(calc(-1 * var(--banner-artwork-center)))',
	'@media (min-width: 30rem)': {
		insetBlockStart: '50%',
		width: 'var(--banner-artwork-width)',
		height: 'auto',
		maxWidth: 'none',
		transform: 'translateY(calc(-1 * var(--banner-artwork-center)))',
	},
});

export const Banner = ({
	title,
	titleDecoration,
	eyebrow,
	description,
	image,
	hasBackgroundColor,
	hasDotGrid = false,
}: {
	title: string;
	titleDecoration?: string;
	/**
	 * Optional label rendered above the title. Styled using the `font.code` token
	 * in uppercase (the "Code" typography token, all caps).
	 */
	eyebrow?: string;
	description: string;
	image?: {
		light: string;
		dark: string;
		alt?: string;
		isFullHeight?: boolean;
		overflowArtwork?: { src?: string; width: number; centerY: number };
	};
	hasBackgroundColor?: boolean;
	hasDotGrid?: boolean;
}): React.JSX.Element => {
	const containerRef = useRef<HTMLDivElement>(null);
	const imageAlt = image?.alt ?? `Banner for ${title} page`;

	const content = (
		<Box xcss={cx(styles.bannerWidth, image?.isFullHeight && styles.fullHeightLayout)}>
			<Box xcss={cx(styles.contentContainer, image?.isFullHeight && styles.fullHeightContent)}>
				<Stack space="space.100">
					{eyebrow && (
						<Box as="div" xcss={styles.eyebrow}>
							{eyebrow.toUpperCase()}
						</Box>
					)}
					{titleDecoration ? (
						<Box xcss={styles.titleRow}>
							<Box xcss={styles.decoratedTitle}>
								<BrandHeading as="h1" size="xxlarge">
									{title}
								</BrandHeading>
							</Box>
							<img src={titleDecoration} alt="" css={styles.titleDecoration} />
						</Box>
					) : (
						<BrandHeading as="h1" size="xxlarge">
							{title}
						</BrandHeading>
					)}
					<Text size="large">{description}</Text>
				</Stack>
			</Box>
			{image?.isFullHeight ? (
				<Box
					xcss={cx(
						styles.imageContainer,
						styles.fullHeightImageContainer,
						image.overflowArtwork && styles.overflowImageContainer,
					)}
				>
					{(['light', 'dark'] as const).map((colorMode) => (
						<picture
							key={colorMode}
							css={[
								colorMode === 'light' && lightImageStyles,
								colorMode === 'dark' && darkImageStyles,
							]}
						>
							{image.overflowArtwork?.src && (
								<source media="(min-width: 30rem)" srcSet={image.overflowArtwork.src} />
							)}
							<img
								css={[styles.fullHeightImage, image.overflowArtwork && overflowArtworkStyles]}
								style={
									{
										'--banner-artwork-width': `${image.overflowArtwork?.width ?? 0}px`,
										'--banner-artwork-center': `${image.overflowArtwork?.centerY ?? 0}px`,
									} as React.CSSProperties
								}
								src={image[colorMode]}
								alt={imageAlt}
							/>
						</picture>
					))}
				</Box>
			) : (
				image && (
					<ImageContainer>
						<div css={lightImageStyles}>
							<Image src={image.light} alt={imageAlt} />
						</div>
						<div css={darkImageStyles}>
							<Image src={image.dark} alt={imageAlt} />
						</div>
					</ImageContainer>
				)
			)}
		</Box>
	);

	return (
		<Box
			xcss={cx(
				styles.bannerContainer,
				hasBackgroundColor && styles.backgroundColor,
				image?.isFullHeight && styles.fullHeightBanner,
			)}
		>
			{hasDotGrid ? (
				<RelativePosition>
					<div ref={containerRef} css={[styles.edgeToEdgeWrapper, customEdgeToEdgeWrapperStyles]}>
						<HeroGridBackground containerRef={containerRef} />
					</div>
					<PageContentContainer>{content}</PageContentContainer>
				</RelativePosition>
			) : (
				<PageContentContainer>{content}</PageContentContainer>
			)}
		</Box>
	);
};

const ImageContainer = ({ children }: { children: ReactNode }) => {
	return <Box xcss={styles.imageContainer}>{children}</Box>;
};
